/**
 * Community Realtime — Canonical Event Types
 *
 * ERRATA §4.6: dot-notation consistently for all event names.
 * Server-authoritative — DB trigger → Edge Function → Broadcast (ERRATA §4.1).
 * Client hooks consume these; the Edge Function (Phase 5) produces them.
 *
 * @see ERRATA §4.1 — broadcast producer architecture
 * @see ERRATA §4.2 — client_temp_id for optimistic reconciliation
 * @see ERRATA §4.6 — event name canonicalization
 */

import { z } from 'zod';
import { messageTypeSchema } from '@hermes/db/schema';

// Inline to avoid circular dependency (community.ts → realtime-events.ts → community.ts)
const senderProfileSchema = z.object({
  full_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
});

// ===========================================
// Event envelope — all events share this shape
// ===========================================

export const realtimeEventEnvelopeSchema = z.object({
  type: z.string(),
  sent_at: z.string(),
  sender_id: z.string().uuid(),
});

// ===========================================
// Conversation channel events
// Channel: conversation:${conversationId}
// ===========================================

/**
 * message.created — emitted server-side by `fn_broadcast_message_events`
 * (hermes migration 468) on every `messages` INSERT, to BOTH the private and
 * the (transitional) public leg of `conversation:<id>`.
 *
 * Mirrors that trigger's payload: the reply preview arrives RESOLVED as
 * `reply_to` (not a bare `reply_to_id`), and `mentions` rides along so a
 * receiver can render mention chips without a refetch. `client_temp_id` is
 * lifted out of `messages.metadata` and is what the sender's own client uses
 * to reconcile its optimistic echo (ERRATA §4.2).
 */
export const messageCreatedPayloadSchema = z.object({
  message: z.object({
    id: z.string().uuid(),
    conversation_id: z.string().uuid(),
    sender_id: z.string().uuid(),
    content: z.string(),
    message_type: messageTypeSchema,
    attachments: z.array(z.unknown()).default([]),
    mentions: z.array(z.unknown()).default([]),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    client_temp_id: z.string().nullable().optional(), // ERRATA §4.2
    reply_to: z
      .object({
        id: z.string().uuid(),
        content: z.string(),
        sender_name: z.string(),
      })
      .nullable(),
    edited_at: z.string().nullable(),
    deleted_at: z.string().nullable(),
    created_at: z.string(),
  }),
  sender_profile: senderProfileSchema,
});
export type MessageCreatedPayload = z.infer<typeof messageCreatedPayloadSchema>;

/** message.updated */
export const messageUpdatedPayloadSchema = z.object({
  message_id: z.string().uuid(),
  content: z.string(),
  edited_at: z.string().nullable(),
  metadata: z.record(z.unknown()).optional(),
});
export type MessageUpdatedPayload = z.infer<typeof messageUpdatedPayloadSchema>;

/** message.deleted */
export const messageDeletedPayloadSchema = z.object({
  message_id: z.string().uuid(),
  deleted_at: z.string(),
});
export type MessageDeletedPayload = z.infer<typeof messageDeletedPayloadSchema>;

/** reaction.added */
export const reactionAddedPayloadSchema = z.object({
  message_id: z.string().uuid(),
  user_id: z.string().uuid(),
  emoji: z.string(),
});
export type ReactionAddedPayload = z.infer<typeof reactionAddedPayloadSchema>;

/** reaction.removed */
export const reactionRemovedPayloadSchema = z.object({
  message_id: z.string().uuid(),
  user_id: z.string().uuid(),
  emoji: z.string(),
});
export type ReactionRemovedPayload = z.infer<typeof reactionRemovedPayloadSchema>;

/** read.updated — ERRATA §2.5: broadcast on conversation channel */
export const readUpdatedPayloadSchema = z.object({
  user_id: z.string().uuid(),
  last_read_message_id: z.string().uuid(),
});
export type ReadUpdatedPayload = z.infer<typeof readUpdatedPayloadSchema>;

// ===========================================
// User channel events
// Channel: user:${userId}
// ===========================================

/**
 * conversation.updated — sidebar preview refresh + unread/toast trigger.
 *
 * Emitted server-side by `fn_broadcast_message_events` (hermes migration 468)
 * to each live participant's PRIVATE `user:<id>` topic. This mirrors that
 * trigger's `jsonb_build_object` exactly — there is no `unread_count` (the
 * client increments locally; a per-recipient count would mean N counting
 * queries inside the INSERT transaction).
 *
 * The trigger always sends `sender_id`; it stays OPTIONAL here so any other
 * (legacy) emitter that omits it degrades to the old behaviour instead of
 * failing validation. The recipient list INCLUDES the sender (so their other
 * tabs stay in sync) — without the field a client cannot tell its own message
 * apart and self-notifies; see the self-filter in useUserChannel.
 */
export const conversationUpdatedPayloadSchema = z.object({
  conversation_id: z.string().uuid(),
  last_message_at: z.string(),
  last_message_preview: z.string(),
  sender_name: z.string(),
  has_mention: z.boolean(),
  sender_id: z.string().uuid().optional(),
  // hermes 647: 'system' marks a group change (created, renamed, recoloured,
  // people added/removed/promoted, left) that only the conversation rows hold.
  message_type: z.string().optional(),
});
export type ConversationUpdatedPayload = z.infer<typeof conversationUpdatedPayloadSchema>;

/** notification.created — the broadcast carries the full notifications row */
export const notificationCreatedPayloadSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  title: z.string(),
  // The row's text column is `message` (this schema said `body` until 2026-08,
  // which nothing ever sent — OS notifications rendered title-only).
  message: z.string().nullable().optional(),
  entity_id: z.string().uuid().optional(),
  created_at: z.string(),
  // Deep-link target written by the emitter; OS notifications fired off this
  // broadcast should land there, not on a generic surface.
  action_url: z.string().nullable().optional(),
});
export type NotificationCreatedPayload = z.infer<typeof notificationCreatedPayloadSchema>;

// ===========================================
// Canonical event names — ERRATA §4.6
// ===========================================

export const REALTIME_EVENTS = {
  // Conversation channel
  MESSAGE_CREATED: 'message.created',
  MESSAGE_UPDATED: 'message.updated',
  MESSAGE_DELETED: 'message.deleted',
  REACTION_ADDED: 'reaction.added',
  REACTION_REMOVED: 'reaction.removed',
  READ_UPDATED: 'read.updated',
  TYPING_START: 'typing.start',
  TYPING_STOP: 'typing.stop',
  // User channel
  CONVERSATION_UPDATED: 'conversation.updated',
  NOTIFICATION_CREATED: 'notification.created',
  // Community-wide channel (community:profiles)
  PROFILE_UPDATED: 'profile.updated',
} as const;

/** Channel every client subscribes to for member name/avatar changes. */
export const COMMUNITY_PROFILES_CHANNEL = 'community:profiles';

/** profile.updated payload — broadcast when a member changes their name/avatar
 *  so every client refreshes that member's avatar/name instantly. */
export const profileUpdatedPayloadSchema = z.object({
  user_id: z.string().uuid(),
  full_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
});
export type ProfileUpdatedPayload = z.infer<typeof profileUpdatedPayloadSchema>;

// ===========================================
// Presence state shapes
// ===========================================

/** Global community presence — ERRATA §6.5: respect presence_visible */
export const presenceStateSchema = z.object({
  user_id: z.string().uuid(),
  display_name: z.string(),
  avatar_url: z.string().nullable(),
  status: z.enum(['online', 'idle', 'offline']),
  active_conversation_id: z.string().uuid().nullable(),
  last_seen: z.string(),
});
export type PresenceState = z.infer<typeof presenceStateSchema>;

/** Typing presence on a conversation channel */
export const typingPresenceSchema = z.object({
  user_id: z.string().uuid(),
  display_name: z.string(),
  is_typing: z.boolean(),
});
export type TypingPresence = z.infer<typeof typingPresenceSchema>;
